import { IconButton } from '@/ui/common/Buttons/IconButton';
import { TWeekDay, TWeekDays } from '@/service/creators/createWeekDays';
import { cn } from '@/service/utils/cn';

type TDayTitleProps = TWeekDay & {
    isActive: boolean;
    onClick: () => void;
    dayIsDisabled?: boolean;
    noActivities?: boolean;
    isToday?: boolean;
};

const DayTitle = ({
    day,
    name,
    shortName,
    isActive,
    onClick,
    dayIsDisabled,
    noActivities,
    isToday,
}: TDayTitleProps) => (
    <div
        id={name}
        className="tx-large-semibold flex w-16 flex-col items-center text-gray-1 dark:text-white md:ml-3 md:w-full md:min-w-[188px] md:max-w-[196px]"
    >
        <div className="hidden md:block">{name}</div>
        <button
            type="button"
            className={cn(
                `tx-heading-regular m-1 flex h-[50px] w-[50px] items-center justify-center rounded-3xl border border-brand text-gray-1 md:tx-large-regular  dark:text-gray-3 md:h-9  md:w-9`,
                !isActive &&
                    !noActivities &&
                    'tx-heading-regular border-brand bg-white text-gray-1 md:tx-large-regular dark:bg-blue-main dark:text-gray-3',
                isActive && 'tx-heading-4 border-brand bg-brand text-white md:tx-large-semibold',
                isToday ? 'font-extrabold' : ''
            )}
            onClick={onClick}
            disabled={dayIsDisabled || isActive}
        >
            {day}
        </button>
        <div
            className={cn(
                `tx-large-medium md:hidden`,
                isActive ? `text-brand` : `text-gray-3 dark:text-dark-1`
            )}
        >
            {shortName}
        </div>
    </div>
);

type TDaysLineProps = {
    week: TWeekDays;
    activeDay: number;
    selectDay: (dayIndex: number) => void;
    goToPrevDay?: () => void;
    goToNextDay?: () => void;
    availList?: boolean[];
    showActionButtons?: boolean;
    prevDayDisabled?: boolean;
    nextDayDisabled?: boolean;
    dayIsDisabled?: boolean;
};

export const DaysLine = ({
    week,
    activeDay,
    selectDay,
    goToPrevDay,
    goToNextDay,
    showActionButtons,
    prevDayDisabled,
    nextDayDisabled,
    dayIsDisabled,
    availList,
}: TDaysLineProps) => (
    <div
        data-test-id="days-line"
        className="no-scrollbar mx-auto flex w-full max-w-[354px] items-center justify-center gap-x-1 md:max-w-[600px]"
    >
        {showActionButtons && (
            <IconButton
                className="mb-4 ml-3 flex items-center disabled:cursor-default md:hidden"
                icon={
                    <div className="h-4 w-4 bg-arrow-left bg-contain bg-no-repeat dark:bg-arrow-left-dark" />
                }
                onClick={goToPrevDay}
                disabled={prevDayDisabled}
                data-test-id="days-line-prev"
            />
        )}
        <div
            className="custom-scroll flex w-full max-w-[275px] items-center justify-center gap-x-3 overflow-x-scroll pl-52 md:max-w-full md:gap-x-0 md:overflow-x-visible md:pl-0"
            id="calendar_days_line"
        >
            {week.map((day, index) => (
                <DayTitle
                    {...day}
                    isActive={activeDay === index}
                    onClick={() => selectDay(index)}
                    key={day.name}
                    dayIsDisabled={dayIsDisabled}
                    noActivities={availList?.[index]}
                    data-test-id={`days-line-day-${day}`}
                    isToday={
                        !index &&
                        day.date.getDate() === new Date().getDate() &&
                        day.date.getMonth() === new Date().getMonth()
                    }
                />
            ))}
        </div>
        {showActionButtons && (
            <IconButton
                className="mb-4 ml-2 flex items-center disabled:cursor-default md:hidden"
                icon={
                    <div className="h-4 w-4 bg-arrow-right bg-contain bg-no-repeat dark:bg-arrow-right-dark" />
                }
                onClick={goToNextDay}
                disabled={nextDayDisabled}
                data-test-id="days-line-next"
            />
        )}
    </div>
);
